import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatISTDateTime } from "../helper";
import { axiosRequest } from "./axios";
import { apiBaseUrl } from "../constant/endpoint";

// Seller details — update here if registration/GST details change.
const SELLER = {
  name: "Corestack Entertainment LLP",
  addressLines: ["Shop No. 206 & 210, Modi Mall 1-2, Sector 25A,", "Noida-201301 Uttar Pradesh"],
  gstin: "09AAWFC3029P1ZO",
  stateName: "Uttar Pradesh",
  stateCode: "09",
  email: "corestackentertainment@gmail.com",
  pan: "AAWFC3029P",
};

// Deposits >= this amount get a single invoice (18% GST) instead of the 70/30 split.
// Must match cmsCLub-dev/helper/invoiceNumber.js's SINGLE_TIER_THRESHOLD.
const SINGLE_TIER_THRESHOLD = 80000;

// Below the threshold, a deposit is split into two invoices: a "Service" invoice
// for the club-entry portion (40% GST) and a "Food" invoice for the F&B portion (5% GST).
const SERVICE_SPLIT = { share: 0.70, gstRate: 0.40, gstEachPercent: 20, sac: "999699", particulars: "Wallet Deposit / Buy-in" };
const FOOD_SPLIT = { share: 0.30, gstRate: 0.05, gstEachPercent: 2.5, sac: "996331", particulars: "Food & Beverages" };

// At/above the threshold, a single invoice covers the full amount at 18% GST.
const SINGLE_SPLIT = { share: 1, gstRate: 0.18, gstEachPercent: 9, sac: "999699", particulars: "Wallet Deposit / Buy-in" };

const a1 = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

const twoDigitsToWords = (n) => (n < 20 ? a1[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + a1[n % 10] : ""));
const threeDigitsToWords = (n) =>
  n >= 100 ? `${a1[Math.floor(n / 100)]} Hundred${n % 100 ? " " + twoDigitsToWords(n % 100) : ""}` : twoDigitsToWords(n);

const numberToWords = (num) => {
  if (num === 0) return "Zero";
  let n = num;
  let words = "";
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  if (crore) words += threeDigitsToWords(crore) + " Crore ";
  if (lakh) words += threeDigitsToWords(lakh) + " Lakh ";
  if (thousand) words += threeDigitsToWords(thousand) + " Thousand ";
  if (n) words += threeDigitsToWords(n);
  return words.trim();
};

const amountInWords = (amount) => {
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  let str = `INR ${numberToWords(rupees)} Rupees`;
  if (paise > 0) str += ` and ${numberToWords(paise)} Paise`;
  return `${str} Only`;
};

const inr = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Splits a GST-inclusive total into a whole-rupee taxable base + CGST/SGST,
// with any residual paise absorbed into a "Round off" line — matching how
// the club's real invoices (see reference PDFs) reconcile to an exact total.
const computeGstBreakup = (amount, gstRate) => {
  const base = Math.floor(amount / (1 + gstRate));
  const cgst = Math.round(base * (gstRate / 2) * 100) / 100;
  const sgst = Math.round(base * (gstRate / 2) * 100) / 100;
  const roundOff = Math.round((amount - base - cgst - sgst) * 100) / 100;
  return { base, cgst, sgst, roundOff };
};

const renderInvoice = ({ txn, invoiceNo, amount, split }) => {
  const { base, cgst, sgst, roundOff } = computeGstBreakup(amount, split.gstRate);

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;
  const contentWidth = pageWidth - marginX * 2;

  doc.setFontSize(13);
  doc.setFont(undefined, "bold");
  doc.text("TAX INVOICE", pageWidth / 2, 45, { align: "center" });

  const sellerBlock =
    `${SELLER.name}\n${SELLER.addressLines.join("\n")}\n\n` +
    `GSTIN/UIN: ${SELLER.gstin}\nState Name: ${SELLER.stateName}, Code: ${SELLER.stateCode}\nE-Mail: ${SELLER.email}`;

  const metaBlock =
    `Invoice No.: ${invoiceNo}\nDated: ${formatISTDateTime(txn.txn_date)}\n\n` +
    `Mode/Terms of Payment: ${txn.payment_mode || "—"}\nReference No.: ${txn._id || "—"}`;

  const buyerBlock = `Buyer (Bill To)\n${txn.user_id?.name || "—"}`;

  autoTable(doc, {
    startY: 60,
    margin: { left: marginX, right: marginX },
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 6, valign: "top", lineColor: [0, 0, 0], lineWidth: 0.75 },
    columnStyles: { 0: { cellWidth: contentWidth * 0.55 }, 1: { cellWidth: contentWidth * 0.45 } },
    body: [
      [{ content: sellerBlock, styles: { fontStyle: "normal" } }, { content: metaBlock }],
      [{ content: buyerBlock }, { content: "" }],
    ],
  });

  const itemRows = [
    ["1", split.particulars, split.sac, inr(base)],
    ["", `Output CGST ${split.gstEachPercent}%`, "", inr(cgst)],
    ["", `Output SGST ${split.gstEachPercent}%`, "", inr(sgst)],
  ];
  if (roundOff !== 0) itemRows.push(["", "Round off", "", inr(roundOff)]);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY,
    margin: { left: marginX, right: marginX },
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 6, lineColor: [0, 0, 0], lineWidth: 0.75 },
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold", lineColor: [0, 0, 0], lineWidth: 0.75 },
    head: [["Sl No.", "Particulars", "SAC", "Amount (Rs.)"]],
    columnStyles: {
      0: { cellWidth: 40, halign: "center" },
      1: { cellWidth: contentWidth - 40 - 70 - 110 },
      2: { cellWidth: 70, halign: "center" },
      3: { cellWidth: 110, halign: "right" },
    },
    body: itemRows,
    foot: [["", "", "Total", `Rs. ${inr(amount)}`]],
    footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold", lineColor: [0, 0, 0], lineWidth: 0.75, halign: "right" },
  });

  let y = doc.lastAutoTable.finalY + 20;
  doc.setFontSize(9);
  doc.setFont(undefined, "bold");
  doc.text("Amount Chargeable (in words):", marginX, y);
  doc.setFont(undefined, "normal");
  doc.text(amountInWords(amount), marginX, y + 14, { maxWidth: contentWidth });

  y += 40;
  doc.setFont(undefined, "bold");
  doc.text(`Company's PAN: ${SELLER.pan}`, marginX, y);
  doc.text(`For ${SELLER.name}`, pageWidth - marginX, y, { align: "right" });

  y += 50;
  doc.setFont(undefined, "normal");
  doc.text("Authorised Signatory", pageWidth - marginX, y, { align: "right" });

  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.text("This is a Computer Generated Invoice", pageWidth / 2, doc.internal.pageSize.getHeight() - 30, { align: "center" });

  doc.save(`invoice_${invoiceNo.replace(/\//g, "-")}.pdf`);
};

export const downloadTransactionInvoice = async (txn, token) => {
  if (txn.txn_type !== "DEPOSIT") return;

  const res = await axiosRequest("GET", `${apiBaseUrl}/transaction/${txn._id}/invoice-numbers`, token);
  if (res?.status !== "success") {
    throw new Error(res?.message || "Failed to fetch invoice numbers");
  }
  if (res.data?.eligible === false) {
    const err = new Error(res.data.reason || "This transaction isn't eligible for a digital invoice.");
    err.notEligible = true;
    throw err;
  }
  const { service: serviceInvoiceNo, food: foodInvoiceNo } = res.data;

  const total = Number(txn.txn_amount || 0);

  if (total >= SINGLE_TIER_THRESHOLD) {
    renderInvoice({ txn, invoiceNo: serviceInvoiceNo, amount: total, split: SINGLE_SPLIT });
    return;
  }

  const serviceAmount = Math.round(total * SERVICE_SPLIT.share * 100) / 100;
  const foodAmount = Math.round((total - serviceAmount) * 100) / 100;

  // Browsers can silently drop the first of two doc.save() downloads fired in the
  // same tick, so stagger them slightly to guarantee both files actually save.
  renderInvoice({ txn, invoiceNo: serviceInvoiceNo, amount: serviceAmount, split: SERVICE_SPLIT });
  setTimeout(() => {
    renderInvoice({ txn, invoiceNo: foodInvoiceNo, amount: foodAmount, split: FOOD_SPLIT });
  }, 400);
};
